import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect } from 'react';
import type { PricingEstimationTotal } from '../../../../types';

interface UseEstimateFormNavigationParams {
	currentStep: number;
	setCurrentStep: Dispatch<SetStateAction<number>>;
	formView: 'form' | 'loading' | 'error';
	setFormView: Dispatch<SetStateAction<'form' | 'loading' | 'error'>>;
	hasContactedVendor: boolean;
	estimate: PricingEstimationTotal | null;
	closeCallback: () => void;
	onEstimateReady?: () => void;
	onEstimateCalculated?: () => void;
	onModalClosed?: () => void;
}

export const useEstimateFormNavigation = ({
	currentStep,
	setCurrentStep,
	formView,
	setFormView,
	hasContactedVendor,
	estimate,
	closeCallback,
	onEstimateReady,
	onEstimateCalculated,
	onModalClosed,
}: UseEstimateFormNavigationParams) => {
	const totalSteps = hasContactedVendor ? 3 : 4;

	const advanceFromLoading = useCallback(() => {
		if (estimate != null) {
			if (hasContactedVendor) {
				onEstimateReady?.();
				onModalClosed?.();
				closeCallback();
			} else {
				onEstimateCalculated?.();
				setCurrentStep(4);
				setFormView('form');
			}
		} else {
			setFormView('error');
		}
	}, [
		estimate,
		hasContactedVendor,
		closeCallback,
		onEstimateReady,
		onEstimateCalculated,
		onModalClosed,
		setCurrentStep,
		setFormView,
	]);

	useEffect(() => {
		if (formView !== 'loading' || currentStep !== 3) return;
		const timer = setTimeout(advanceFromLoading, 1000);
		return () => clearTimeout(timer);
	}, [formView, currentStep, advanceFromLoading]);

	const handleBack = () => {
		if (currentStep > 1) setCurrentStep((s) => s - 1);
	};

	const handleNext = () => {
		if (currentStep === 3 && hasContactedVendor) {
			setFormView('loading');
			return;
		}
		if (currentStep < totalSteps) setCurrentStep((s) => s + 1);
	};

	// If the user already contacted the vendor but the conversations had not
	// yet loaded when the modal was opened, the user can land on step 4
	// (contact form) before this hook knows `hasContactedVendor` is true.
	// The contact form would attempt to send a new RFQ, which fails for an
	// already-contacted vendor. Catch that case and route back through the
	// loading step so the modal closes with the persisted estimate.
	useEffect(() => {
		if (hasContactedVendor && currentStep === 4) {
			setCurrentStep(3);
			setFormView('loading');
		}
	}, [hasContactedVendor, currentStep, setCurrentStep, setFormView]);

	return { totalSteps, handleBack, handleNext };
};
